import React, {useEffect} from "react";
import {useLocation} from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import TextField from "@mui/material/TextField";
import AlertTitle from "@mui/material/AlertTitle";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Select from "@mui/material/Select";
import InfoIcon from "@mui/icons-material/Info";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import {useTheme} from "@mui/material/styles";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import {useTranslation} from "react-i18next";
import {fetchData, postData} from "../../utils/api";
import {UserContext} from "../../App";
import ExperimentProcessingStatus from "./ExperimentProcessingStatus";
import ExperimentPreview from "./ExperimentPreview";
import {getSelectorStyles} from "../../utils/table-utils";

const Alert = React.forwardRef((props, ref) => (
    <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
));

export default function ExperimentConfigurator() {
    const theme = useTheme();
    // init internationalization
    const {t} = useTranslation();
    // needed for form validation
    const formRef = React.useRef();

    function useQuery() {
        const {search} = useLocation();
        return React.useMemo(() => new URLSearchParams(search), [search]);
    }

    // get type from URL query
    const experimentType = useQuery().get("type");

    // get data from current dataset
    const {datasets, currentDatasetIdx, fetchExperiments} =
        React.useContext(UserContext);

    const [baseData, setBaseData] = React.useState([""]);
    const [processingStarted, setProcessingStarted] = React.useState(false);
    const [columnNames, setColumnNames] = React.useState([""]);
    const [columnMapping, setColumnMapping] = React.useState({});
    const [experimentName, setExperimentName] = React.useState("");
    // validation errors
    const [errors, setErrors] = React.useState({});

    const fetchDataset = () => {
        fetchData(
            `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}?num_entries=10`
        ).then((data) => {
            // set datasets and use first dataset as default
            setBaseData(data.baseData);
            setColumnNames(Object.keys(data.baseData));
            // create column Mapping, to give each column a purpose (feature, target, id etc.) The default purpose is "feature"
            const mapping = Object.keys(data.baseData).reduce((obj, name) => {
                obj[name] = {type: "feature"};
                return obj;
            }, {});
            setColumnMapping(mapping);
        });
    };

    // set base Data by API requests, when the current dataset is gathered
    useEffect(() => {
        if (datasets.length >= 1) {
            fetchDataset();
        }
        setExperimentName(t(
                    "experimentTypeSelector.ExperimentNameStart") + t(`experimentTypeSelector.${experimentType}Title`) + t(
                    "experimentTypeSelector.ExperimentNameEnd"),);
    }, [datasets, currentDatasetIdx]);

    // Error Snackbars
    const [snackbarSettings, setSnackbarSettings] = React.useState({
        isOpen: false,
        type: "info",
        message: "",
    });

    // Error Alert
    const [alertSettings, setAlertSettings] = React.useState({
        isVisible: false,
        type: "error",
        message: "",
    });

    function getKeysArray(columnTypeName) {
        /**
         * Helper Method, that uses the column mapping and returns an array list of keys by a given col type
         * e.g. if you want all "feature" columns, the value would be "feature"
         * */
        return Object.entries(columnMapping)
            .filter(([, value]) => value.type === columnTypeName)
            .map(([key]) => key);
    }

    const handleProcessSubmit = () => {
        setAlertSettings({
            isVisible: false,
            type: "error",
            message: "",
        })

        // set snackbar notification, dependent on post status
        const snackbar = {...snackbarSettings};
        postData(
            `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}/experiment`,
            {
                experimentName,
                experimentType,
                columnMapping,
            }
        ).then((data) => {
            // set snackbar notification, dependent on post status
            snackbar.message = data.data;
            if (data.status === 201) {
                setErrors({});
                snackbar.isOpen = true;
                snackbar.type = "success";
                snackbar.message = t(
                    "experimentConfigurator.SuccessExperimentProcessingStarted"
                );
                // snackbar experiment list and set current experiment to just created
                fetchExperiments({latest: experimentName});
                // then start processing: first rigger processing start via API
                postData(
                    `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}/experiment/${experimentName}/processing`,
                    {}
                ).then((experimentData) => {
                    if (experimentData.status === 200) {
                        // if processing was started, change website state to process-view
                        setProcessingStarted(true);
                    } else {
                        snackbar.type = "error";
                    }
                });
            } else {
                snackbar.type = "error";
                if (data.status === 422) {
                    // write error details to form
                    setErrors(data.detail);
                    setAlertSettings({
                        isVisible: true,
                        type: "error",
                        message: t("experimentErrors.FailedCreationError"),
                    })
                    // snackbar.message = t(
                    //         "experimentConfigurator.ErrorTargetDoesNotMatchExperimentType"
                    //     );

                    // if (data.detail.experimentType?.message) {
                    //     snackbar.isOpen = true;
                    //     snackbar.type = "error";
                    //     // show snackbar error if experiment type was wrong
                    //     snackbar.message = t(
                    //         "experimentConfigurator.ErrorTargetDoesNotMatchExperimentType"
                    //     );
                    // }
                }
                if (data.status === 409) {
                    // write error details to form
                    setErrors(data.detail);

                    snackbar.message = t(
                        "experimentConfigurator.ErrorExperimentAlreadyExists"
                    );
                }
            }
        });

        setSnackbarSettings(snackbar);
    };

    useEffect(() => {
    }, [alertSettings])
    const handleExperimentNameChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        // reset error message
        if (errors.hasOwnProperty("experimentName")) {
            const errorsCopy = {...errors};
            delete errorsCopy.experimentName;
            setErrors(errorsCopy);
        }
        setExperimentName(event.target.value);
    };
    const handleSelectorChange = (selectorName) => (event) => {
        // reset error message
        if (errors.hasOwnProperty(selectorName)) {
            const errorsCopy = {...errors};
            delete errorsCopy[selectorName];
            setErrors(errorsCopy);
        }

        const mappingCopy = {...columnMapping};
        // check if old value exists, if so, reset it as feature
        if (getKeysArray(selectorName).length === 1) {
            mappingCopy[getKeysArray(selectorName)[0]].type = "feature";
        }
        // set selected item as type of the selector e.g. "timestamp"
        if (event.target.value !== "None") {
            mappingCopy[event.target.value].type = selectorName;
        }
        setColumnMapping(mappingCopy);
    };
    const handleMultiSelectorChange = (selectorName) => (event) => {
        // reset error message
        if (errors.hasOwnProperty(selectorName)) {
            const errorsCopy = {...errors};
            delete errorsCopy[selectorName];
            setErrors(errorsCopy);
        }
        const {value} = event.target;
        const mappingCopy = {...columnMapping};

        if (value.includes("Select All")) {
            // check if all are already Selected or not, if yes then unselect all
            if (Object.values(columnMapping).filter(column => column.type === "ignored").length === Object.values(columnMapping).length) {
                Object.values(columnMapping).map(column => {
                    column.type = "feature"
                })
            } else {
                Object.values(columnMapping).map(column => {
                    column.type = selectorName
                })
            }
        } else {
            // check if an Item was removed or added
            const removedEntries = getKeysArray(selectorName).filter(
                (x) => event.target.value.indexOf(x) === -1
            );
            if (removedEntries.length === 0) {
                mappingCopy[event.target.value.at(-1)].type = selectorName;
            } else {
                mappingCopy[removedEntries.at(-1)].type = "feature";
            }
        }
        setColumnMapping(mappingCopy);
    };

    return datasets.length >= 1 ? (
        <>
            <Snackbar open={snackbarSettings.isOpen} autoHideDuration={8000}>
                <Alert severity={snackbarSettings.type}>
                    {snackbarSettings.message}
                </Alert>
            </Snackbar>
            {processingStarted ? (
                // if processing was already initialized,show status
                <ExperimentProcessingStatus experimentName={experimentName}/>
            ) : (
                // else render experiment configuration
                <form ref={formRef}>
                    <Grid
                        id="experiment-configurator-container"
                        container
                        spacing={2}
                        sx={{padding: theme.spacing(4, 1)}}
                    >
                        <Grid item lg={3} xs={12}>
                            <Typography variant="h4">
                                {t("experimentConfigurator.PageTitle_1")} {experimentType}{" "}
                                {t("experimentConfigurator.PageTitle_2")}
                                <em>
                                    {datasets.length
                                        ? datasets[currentDatasetIdx].datasetName
                                        : ""}
                                </em>
                            </Typography>
                            <br/>
                            <Grid container rowSpacing={3}>
                                <Grid item xs={12}>
                                    <Typography variant="h6">
                                        {t("experimentConfigurator.TargetTitle")}
                                    </Typography>
                                    <Typography variant="body2">
                                        {t("experimentConfigurator.TargetDescription")}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl required error={Boolean(errors.target)}>
                                        <InputLabel>
                                            {t("experimentConfigurator.TargetDropdown")}
                                        </InputLabel>
                                        <Select
                                            multiple
                                            value={getKeysArray("target")}
                                            label="Target Columns(s)"
                                            onChange={handleMultiSelectorChange("target")}
                                            input={
                                                <OutlinedInput id="select-multiple-chip" label="Chip"/>
                                            }
                                            renderValue={(selected) => (
                                                <Box
                                                    sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}
                                                >
                                                    {selected.map((value) => (
                                                        <Chip key={`target-${value}`} label={value}/>
                                                    ))}
                                                </Box>
                                            )}
                                            MenuProps={theme.multiSelectMenuProps}
                                            sx={{minWidth: 200}}
                                        >
                                            {columnNames.map((name) => (
                                                <MenuItem
                                                    key={name}
                                                    value={name}
                                                    style={getSelectorStyles(name, columnNames, theme)}
                                                >
                                                    {name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            {t(
                                                `experimentConfigurator.Error ${errors.target?.message}`
                                            )}
                                        </FormHelperText>
                                    </FormControl>
                                    <Tooltip title={t("experimentConfigurator.HintTarget")}>
                                        <IconButton>
                                            <InfoIcon/>
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h6">
                                        {t("experimentConfigurator.FeatureTitle")}
                                    </Typography>
                                    <Typography variant="body2">
                                        {t("experimentConfigurator.FeatureDescription")}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl error={Boolean(errors.ignored)}>
                                        <InputLabel>
                                            {t("experimentConfigurator.IgnoreDropdown")}
                                        </InputLabel>
                                        <Select
                                            multiple
                                            value={
                                                getKeysArray("ignored")
                                            }
                                            label="Ignored Column(s)"
                                            onChange={handleMultiSelectorChange("ignored")}
                                            input={
                                                <OutlinedInput id="select-ingore-cols" label="Chip"/>
                                            }
                                            renderValue={(selected) => (
                                                <Box
                                                    sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}
                                                >
                                                    {selected.map((value) => (
                                                        <Chip key={`ignored-${value}`} label={value}/>
                                                    ))}
                                                </Box>
                                            )}
                                            MenuProps={theme.multiSelectMenuProps}
                                            sx={{minWidth: 200, maxWidth: 520}}
                                        >
                                            <MenuItem value="Select All"
                                                      sx={{fontWeight: theme.typography.fontWeightMedium}}><b>Select
                                                All</b></MenuItem>
                                            {columnNames.map((name) => (
                                                <MenuItem
                                                    key={name}
                                                    value={name}
                                                    style={getSelectorStyles(name, columnNames, theme)}
                                                >
                                                    {name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            {t(
                                                `experimentConfigurator.Error ${errors.ignored?.message}`
                                            )}
                                        </FormHelperText>
                                    </FormControl>
                                    <Tooltip title={t("experimentConfigurator.HintIgnore")}>
                                        <IconButton>
                                            <InfoIcon/>
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl error={Boolean(errors.id)}>
                                        <InputLabel>
                                            {t("experimentConfigurator.IdDropdown")}
                                        </InputLabel>
                                        <Select
                                            // set None as default
                                            value={
                                                getKeysArray("id").length > 0
                                                    ? getKeysArray("id")
                                                    : ["None"]
                                            }
                                            onChange={handleSelectorChange("id")}
                                            label="ID Column"
                                            sx={{minWidth: 200}}
                                        >
                                            <MenuItem value="None">
                                                <em>{t("experimentConfigurator.IdDropdown")}</em>
                                            </MenuItem>
                                            {columnNames.map((name) => (
                                                <MenuItem
                                                    key={`id-${name}`}
                                                    value={name}
                                                    style={getSelectorStyles(name, columnNames, theme)}
                                                >
                                                    {name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            {t(`experimentConfigurator.Error ${errors.id?.message}`)}
                                        </FormHelperText>
                                    </FormControl>
                                    <Tooltip title={t("experimentConfigurator.HintID")}>
                                        <IconButton>
                                            <InfoIcon/>
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl error={Boolean(errors.timestamp)}>
                                        <InputLabel>
                                            {t("experimentConfigurator.TimestampDropdown")}
                                        </InputLabel>
                                        <Select
                                            // set None as default
                                            value={
                                                getKeysArray("timestamp").length > 0
                                                    ? getKeysArray("timestamp")
                                                    : ["None"]
                                            }
                                            onChange={handleSelectorChange("timestamp")}
                                            label="Timestamp Column"
                                            sx={{minWidth: 200}}
                                        >
                                            <MenuItem value="None">
                                                <em>{t("experimentConfigurator.NoTimestamp")}</em>
                                            </MenuItem>
                                            {columnNames.map((name) => (
                                                <MenuItem
                                                    key={`timestamp-${name}`}
                                                    value={name}
                                                    style={getSelectorStyles(name, columnNames, theme)}
                                                >
                                                    {name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            {t(
                                                `experimentConfigurator.Error ${errors.timestamp?.message}`
                                            )}
                                        </FormHelperText>
                                    </FormControl>
                                    <Tooltip title={t("experimentConfigurator.HintTime")}>
                                        <IconButton>
                                            <InfoIcon/>
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h6">
                                        {t("experimentConfigurator.ExperimentSettingsTitle")}
                                    </Typography>
                                    <Typography variant="body2">
                                        {t("experimentConfigurator.ExperimentSettingsDescription")}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl>
                                        <TextField
                                            label={t("experimentConfigurator.ExperimentNameFillable")}
                                            value={`${experimentName}`}
                                            onChange={handleExperimentNameChange}
                                            error={Boolean(errors.experimentName)}
                                            helperText={t(
                                                `experimentConfigurator.Error ${errors.experimentName?.message}`
                                            )}
                                        />
                                    </FormControl>
                                    <Tooltip
                                        title={t("experimentConfigurator.HintExperimentName")}
                                    >
                                        <IconButton>
                                            <InfoIcon/>
                                        </IconButton>
                                    </Tooltip>
                                </Grid>

                            </Grid>

                            {alertSettings.isVisible && (
                                <Alert severity={alertSettings.type} sx={{marginTop: "10px"}}>
                                    <AlertTitle>{alertSettings.message}</AlertTitle>
                                    {errors && (
                                        <div>
                                            {Object.keys(errors).map(error => (
                                                <div
                                                    key={error}> - {t(`experimentErrors.${error}`, errors[error])}</div>
                                            ))}
                                        </div>)}
                                </Alert>
                            )}

                        </Grid>
                        {/* LEFT SIDE */}
                        <Grid item lg={9} xs={12}>
                            <ExperimentPreview
                                columnMapping={columnMapping}
                                baseData={baseData}
                                getKeysArray={getKeysArray}
                            />
                        </Grid>
                        <Grid item lg={9}  xs={6} />
                        <Grid item lg={3} xs={6} sx={{alignSelf: "end"}}>
                            <Button
                                color="primary"
                                size="medium"
                                variant="contained"
                                onClick={() => {
                                    if (formRef.current.reportValidity()) {
                                        // check required fields
                                        handleProcessSubmit();
                                    }
                                }}
                            >
                                {t("experimentConfigurator.SaveAndProcessButton")}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            )}
        </>
    ) : (
        // if user lands on this page, without having at least one dataset, show this hint
        <Grid item xs={3} sx={{margin: theme.spacing(6)}}>
            <Typography variant="body">
                {t("experimentConfigurator.HintNoDatasetYet")}
            </Typography>
        </Grid>
    );
}
